/**
 * The chat drafts behind a checkout notice's "Ask Elara" (ELARA-1079).
 *
 * Every checkout notice that ends in "Ask Elara…" hands the traveler to the trip's chat with one of these
 * in the composer, UNSENT: it names the rooms, the property and the stay, so Elara gets the whole question
 * without the traveler retyping what the checkout already knew, and the traveler can still add what they
 * would accept instead before it goes anywhere. The caller attaches the hotel alongside.
 */

import { formatDate } from './date-utils.js';

export interface DraftStay {
  hotelName?: string;
  /** ISO `YYYY-MM-DD` stay dates. */
  checkInDate: string;
  checkOutDate: string;
}

/** Rooms the checkout dropped as no longer available at the rate the traveler chose. */
export function unavailableRoomsDraft(input: DraftStay & { roomNames: string[] }): string {
  const many = input.roomNames.length !== 1;
  const rooms = input.roomNames.length > 0 ? roomsPhrase(input.roomNames) : 'the rooms I picked';
  return sentence(
    [rooms, wherePhrase(input), many ? 'are' : 'is', `no longer available at the ${many ? 'rates' : 'rate'} I chose.`],
    `Can you find me ${many ? 'alternatives' : 'an alternative'}?`,
  );
}

/** Rooms dropped because the hotel has fewer of that type than the traveler asked for. */
export function roomCountDraft(input: DraftStay & { roomName: string; available: number; dropped: number }): string {
  const only = input.available === 1 ? `only 1 ${input.roomName} is` : `only ${input.available} ${input.roomName}s are`;
  const more = input.dropped === 1 ? 'another room' : `${input.dropped} more rooms`;
  return sentence(
    [only, 'available', wherePhrase(input), `and I wanted ${input.available + input.dropped}.`],
    `Can you find me ${more}?`,
  );
}

/** An order that booked some rooms and not others (no rollback): the booked rooms stay, the rest need a plan. */
export function partlyBookedDraft(input: DraftStay & { bookedNames: string[]; notBookedNames: string[] }): string {
  const bookedMany = input.bookedNames.length > 1;
  const booked = input.bookedNames.length > 0 ? roomsPhrase(input.bookedNames) : 'part of my stay';
  const notBookedMany = input.notBookedNames.length > 1;
  const notBooked =
    input.notBookedNames.length > 0
      ? roomsPhrase(input.notBookedNames, { otherThan: input.bookedNames })
      : 'one of the rooms';
  return sentence(
    [booked, wherePhrase(input), bookedMany ? 'are' : 'is', `booked, but ${notBooked} couldn’t be booked.`],
    `Can you find me ${notBookedMany ? 'alternatives for them' : 'an alternative for it'}?`,
  );
}

/**
 * "the Conrad Suite", "the 2 Conrad Suites and the King Room". A room type that also appears in
 * `otherThan` reads as "the other Conrad Suite", so a partly-booked pair of the same type does not come
 * out as "the Conrad Suite is booked, but the Conrad Suite couldn’t be booked".
 */
function roomsPhrase(names: string[], options: { otherThan?: string[] } = {}): string {
  const counts = new Map<string, number>();
  for (const name of names) counts.set(name, (counts.get(name) ?? 0) + 1);
  const otherThan = new Set(options.otherThan ?? []);
  const phrases = [...counts].map(([name, count]) => {
    const other = otherThan.has(name) ? 'other ' : '';
    return count > 1 ? `the ${other}${count} ${name}s` : `the ${other}${name}`;
  });
  return phrases.length <= 1 ? (phrases[0] ?? '') : `${phrases.slice(0, -1).join(', ')} and ${phrases.at(-1)}`;
}

/** "at Conrad London St James from October 12th to 13th" — either half may be missing. */
function wherePhrase({ hotelName, checkInDate, checkOutDate }: DraftStay): string {
  const stay = stayPhrase(checkInDate, checkOutDate);
  return [hotelName ? `at ${hotelName}` : '', stay ? `from ${stay}` : ''].filter(Boolean).join(' ');
}

/** "October 12th to 13th", "October 30th to November 2nd". No year: the trip in the chat carries it. */
function stayPhrase(checkInDate: string, checkOutDate: string): string {
  if (!checkInDate || !checkOutDate) return '';
  const sameMonth = checkInDate.slice(0, 7) === checkOutDate.slice(0, 7);
  return `${formatDate(checkInDate, 'MMMM do')} to ${formatDate(checkOutDate, sameMonth ? 'do' : 'MMMM do')}`;
}

function sentence(parts: string[], question: string): string {
  const body = parts.filter(Boolean).join(' ');
  return `${body.charAt(0).toUpperCase()}${body.slice(1)} ${question}`;
}
